import React, { useState } from 'react';
import logo from './EVELOGO.png';

const PROJECTS = [
  {
    id: 'hire-me',
    name: 'PixelGIF',
    blurb: 'Hire Me!',
    // White "Hire Me!" rendered in the game's pixelfont (baked SVG).
    labelImage: `${process.env.PUBLIC_URL}/img/hire-me-v2.svg`,
    selectable: true,
  },
  {
    id: 'pixelgrid',
    name: 'PixelGrid',
    href: '/pixelgrid/',
    blurb: 'Pixel art studio in the browser',
    // Under /img/ because nginx prefix-matches /pixelgrid* to the
    // PixelGrid app proxy.
    logoImage: `${process.env.PUBLIC_URL}/img/pixelgrid-logo.png`,
    titleImage: `${process.env.PUBLIC_URL}/img/pixelgrid-title.png`,
    lightCard: true,
    selectable: true,
    details: {
      title: 'PixelGrid',
      period: 'November, 2025 to Present',
      description: 'PixelGrid is a React Web App that uses html grids written in JSX.',
      sections: [
        {
          heading: 'Frontend (Client)',
          items: [
            'React 18.2.0 with the Latest Stable Version',
            'Deployed to GitHub Pages for Front-end Hosting',
            'The PixelGrid App does not use a database on GitHub Pages.',
          ],
        },
        {
          heading: 'Backend (Server) & Database',
          items: [
            'Node.js with Express.js 4.18.2 — REST API server',
            'MySQL2 3.6.0 — MySQL database driver',
            'MySQL 8.0 — Relational database',
          ],
        },
      ],
    },
  },
  {
    id: 'pixelgame',
    name: "Eve's MMO",
    href: '/pixelgame/',
    blurb: '3D voxel MMO & world editor',
    image: `${process.env.PUBLIC_URL}/Eve's MMO Logo.png`,
    selectable: true,
    details: {
      title: "Eve's MMO",
      period: '2026 to Present',
      description:
        "Eve's MMO is a 3D voxel MMO and world editor that grew out of PixelGrid — build the world in the same editor you play in.",
      sections: [
        {
          heading: 'Frontend (Client)',
          items: [
            'React 18 with a Three.js voxel renderer',
            'Custom pixel-font UI, splash screen, and minimap',
          ],
        },
        {
          heading: 'Backend (Server) & Database',
          items: [
            'Node.js with Express.js — REST API server',
            'Socket.IO — real-time multiplayer world sync',
            'MySQL 8.0 — Relational database',
          ],
        },
      ],
      actions: [
        { label: 'Documentation', href: '/pixelgame/docs' },
        { label: 'Updates - Features', href: '/pixelgame/updates' },
        { label: 'Asset Library', href: '/pixelgame/assets' },
      ],
    },
  },
];

const SOCIALS = [
  { name: 'GitHub', href: 'https://github.com/evepanzarino' },
  { name: 'YouTube', href: 'https://youtube.com/@evepanzarino' },
  { name: 'Instagram', href: 'https://instagram.com/evepanzarino/' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/in/evepanzarino/' },
];

const CONTACT_EMAIL = 'evepanzarino@outlook.com';
const CONTACT_PHONE = '+1 561-767-6146';

function CardArt({ project }) {
  if (project.image) {
    return (
      <img
        className="project-card-image"
        src={project.image}
        alt={`${project.name} — ${project.blurb}`}
      />
    );
  }
  if (project.labelImage) {
    return (
      <span className="project-card-composite">
        <img
          className="project-card-label"
          src={project.labelImage}
          alt={`${project.name} — ${project.blurb}`}
        />
      </span>
    );
  }
  if (project.logoImage) {
    return (
      <span className="project-card-composite">
        <img className="project-card-logo" src={project.logoImage} alt="" />
        <img
          className="project-card-title"
          src={project.titleImage}
          alt={`${project.name} — ${project.blurb}`}
        />
      </span>
    );
  }
  return (
    <>
      <span className="project-name">{project.name}</span>
      <span className="project-blurb">{project.blurb}</span>
    </>
  );
}

function ContactPanel() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('bad status');
      setStatus('sent');
      setForm({ name: '', email: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact-panel">
      <div className="contact-details">
        <div className="contact-item">
          <span className="contact-label">Email Me:</span>
          <a className="contact-line" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
        <div className="contact-item">
          <span className="contact-label">Call Me:</span>
          <a className="contact-line" href={`tel:${CONTACT_PHONE.replace(/[^+\d]/g, '')}`}>{CONTACT_PHONE}</a>
        </div>
      </div>
      <form className="contact-form" onSubmit={submit}>
        <label>
          Name
          <input type="text" value={form.name} onChange={update('name')} maxLength={128} autoComplete="name" />
        </label>
        <label>
          Email
          <input type="email" value={form.email} onChange={update('email')} maxLength={254} autoComplete="email" />
        </label>
        <label>
          Message
          <textarea value={form.message} onChange={update('message')} rows={4} maxLength={5000} required />
        </label>
        <button type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Send Message'}
        </button>
        {status === 'sent' && <p className="contact-status ok">Message sent — thank you!</p>}
        {status === 'error' && (
          <p className="contact-status err">
            Could not send right now — email me instead at {CONTACT_EMAIL}.
          </p>
        )}
      </form>
    </div>
  );
}

function ProjectDetails({ project }) {
  const { details } = project;
  return (
    <div className="project-details">
      <div className="project-details-head">
        <div>
          <h3 className="project-details-title">{details.title}</h3>
          <p className="project-details-period">{details.period}</p>
        </div>
        <a className="open-app-btn" href={project.href}>Open App</a>
      </div>
      <p className="project-details-copy">{details.description}</p>
      <div className="project-details-sections">
        {details.sections.map((section) => (
          <div key={section.heading} className="project-details-section">
            <h4>{section.heading}</h4>
            <ul>
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {details.actions && (
        <div className="project-action-grid" aria-label={`${details.title} links`}>
          {details.actions.map((action) => (
            <a key={action.href} className="project-action-btn" href={action.href}>
              {action.label}
            </a>
          ))}
        </div>
      )}
      {details.github && (
        <p className="project-details-copy">
          GitHub:{' '}
          <a className="project-details-link" href={details.github.href} target="_blank" rel="noreferrer">
            {details.github.label}
          </a>
        </p>
      )}
    </div>
  );
}

export default function App() {
  const [selectedId, setSelectedId] = useState('hire-me');
  const selectedProject = PROJECTS.find((p) => p.id === selectedId) || null;

  // First click selects a card (opens its detail box); clicking the
  // already-selected card again opens the app itself.
  const handleCardClick = (project) => {
    if (selectedId === project.id && project.href) {
      window.location.href = project.href;
      return;
    }
    setSelectedId(project.id);
  };

  return (
    <div className="page">
      <header className="hero">
        <img className="hero-logo" src={logo} alt="Eve — pixel logo" />
        <p className="tagline">
          Eve Panzarino-Hankins
          <br />
          Web Developer &amp; Designer
        </p>
      </header>

      <main className="content">
        <h2 className="section-title">Projects</h2>
        <div className="project-grid">
          {PROJECTS.map((project) => {
            const classes = [
              'project-card',
              project.image || project.logoImage || project.labelImage ? 'has-image' : '',
              project.lightCard ? 'light' : '',
              project.selectable && selectedId === project.id ? 'selected' : '',
            ].filter(Boolean).join(' ');
            return project.selectable ? (
              <button
                key={project.id}
                type="button"
                className={classes}
                aria-pressed={selectedId === project.id}
                title={selectedId === project.id && project.href ? `Open ${project.name}` : project.name}
                onClick={() => handleCardClick(project)}
              >
                <CardArt project={project} />
              </button>
            ) : (
              <a key={project.id} className={classes} href={project.href}>
                <CardArt project={project} />
              </a>
            );
          })}
        </div>

        {selectedId === 'hire-me' && (
          <section className="detail-box" aria-label="Contact Eve">
            <ContactPanel />
          </section>
        )}
        {selectedProject?.details && (
          <section className="detail-box" aria-label={`${selectedProject.name} details`}>
            <ProjectDetails project={selectedProject} />
          </section>
        )}
      </main>

      <footer className="footer">
        <nav className="socials" aria-label="Social links">
          {SOCIALS.map((social) => (
            <a key={social.name} href={social.href} target="_blank" rel="noreferrer">
              {social.name}
            </a>
          ))}
        </nav>
        <p className="footer-note">
          © {new Date().getFullYear()} Eve Panzarino-Hankins
        </p>
      </footer>
    </div>
  );
}
